import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { JetSportBox } from "../../components/BoxView/JetSportBox";
import { BoxContent } from "../../components/BoxView/BoxContent";
import { BoxFooter } from "../../components/BoxView/BoxFooter";
import { BoxHeader2 } from "../../components/BoxView/BoxHeader2";
import IconButton from "../../components/LogoButton/IconButton";
import { withToasts } from "../../components/Toast/Toast";
import { LogoPdfGenerator } from "../../../utils/functions/pdf-generator.service";
import { BaseLogo } from "../../../core/models/entities/BaseLogo";
import { Design } from "../../../core/models/entities/Design";
import { LoadingFrame, InvalidRoute } from "./ExternalApprovalFlow";

type ProofKind = "logo" | "design";

interface ProofPdfTokenPayload {
  v: number;
  t: ProofKind;
  ids: string[];
  vid: number;
  logo: string | null;
}

const TOKEN_VERSION = 1;
const INTERNAL_API_URL = process.env.REACT_APP_API;
const JS_UPLOAD_FUNCTION_KEY = process.env.REACT_APP_JS_UPLOAD_FUNCTION_KEY;
const ENCRYPTION_KEY = process.env.REACT_APP_PROOF_PDF_ENCRYPTION_KEY;

export function ProofPdfDownloadScreen() {
  const [qp] = useSearchParams();
  const token = qp.get("token");

  const [payload, setPayload] = useState<ProofPdfTokenPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("Missing token");
      return;
    }
    if (!ENCRYPTION_KEY) {
      setError("Encryption key not configured");
      return;
    }

    let cancelled = false;
    decryptToken(token, ENCRYPTION_KEY)
      .then((decoded) => {
        if (cancelled) return;
        if (
          !decoded ||
          decoded.v !== TOKEN_VERSION ||
          (decoded.t !== "design" && decoded.t !== "logo") ||
          !Array.isArray(decoded.ids) ||
          decoded.ids.length === 0
        ) {
          setError("Invalid token");
          return;
        }
        setPayload(decoded);
      })
      .catch((err) => {
        console.error("Failed to decrypt proof token:", err);
        if (!cancelled) setError("Invalid token");
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleDownload = async () => {
    if (!payload) return;
    if (!INTERNAL_API_URL) {
      console.error("Internal API URL not configured");
      return;
    }

    const vendorLogoUrl = payload.logo ?? undefined;
    const isLogo = payload.t === "logo";

    try {
      await withToasts(
        async () => {
          const route = isLogo
            ? `${INTERNAL_API_URL}/js-upload/logos/for-pdf`
            : `${INTERNAL_API_URL}/js-upload/designs/for-pdf`;
          const body = isLogo
            ? { productNumbers: payload.ids }
            : { muls: payload.ids };

          const headers: Record<string, string> = {
            "Content-Type": "application/json",
          };
          if (JS_UPLOAD_FUNCTION_KEY) {
            headers["x-functions-key"] = JS_UPLOAD_FUNCTION_KEY;
          }

          const response = await fetch(route, {
            method: "POST",
            headers,
            body: JSON.stringify(body),
          });

          if (!response.ok) {
            throw new Error(`Failed to load PDF data: HTTP ${response.status}`);
          }

          // Items match JsUploadLogoPdfDto / JsUploadDesignPdfDto — the comment at
          // js-kitmaker-api-dev/src/Functions/JsUploadFunctions.cs:3830-3832 says
          // they're shaped for direct consumption by LogoPdfGenerator.
          const items = (await response.json()) as unknown[];

          if (isLogo) {
            await LogoPdfGenerator.generateMultipleLogosPdf(
              items as unknown as BaseLogo[],
              vendorLogoUrl
            );
          } else {
            await LogoPdfGenerator.generateMultipleDesignsPdf(
              items as unknown as Design[],
              vendorLogoUrl
            );
          }
        },
        isLogo
          ? "Generating logo PDF..."
          : "Generating design PDF. This might take a while...",
        isLogo
          ? "Logo PDF generated successfully!"
          : "Design PDF generated successfully!",
        isLogo
          ? "Failed to generate logo PDF"
          : "Failed to generate design PDF",
        setIsDownloading
      );
    } catch (e) {
      console.error("Error downloading PDF:", e);
    }
  };

  if (error) return <InvalidRoute />;
  if (!payload) return <LoadingFrame />;

  const itemCount = payload.ids.length;
  const subject = payload.t === "logo" ? "logo" : "design";
  const subjectPlural = payload.t === "logo" ? "logos" : "designs";

  return (
    <JetSportBox>
      <BoxHeader2 />
      <BoxContent className="items-center text-center pt-32 pb-24 space-y-6">
        <div className="text-[28px] font-bold">Proof PDF ready</div>
        <div className="text-[16px]">
          {itemCount} {itemCount === 1 ? subject : subjectPlural} will be
          included in the PDF.
        </div>
        <div>
          <IconButton
            icon={<FontAwesomeIcon icon={faDownload} />}
            onClick={handleDownload}
            disabled={isDownloading}
          >
            {isDownloading ? "DOWNLOADING..." : "DOWNLOAD PDF"}
          </IconButton>
        </div>
      </BoxContent>
      <BoxFooter />
    </JetSportBox>
  );
}

// AES-256-CBC decryption matching the external API's EncryptionHelper:
//   - URL-safe Base64 input is converted back to standard Base64
//   - First 16 bytes are the IV, the rest is the ciphertext
//   - Key is the UTF-8 bytes of the configured key string, space-padded/truncated to 32 bytes
//   - PKCS7 padding (Web Crypto AES-CBC default, matches .NET Aes.Create() default)
//   - UTF-8 decoded plaintext is parsed as JSON
async function decryptToken(
  urlSafeToken: string,
  keyString: string
): Promise<ProofPdfTokenPayload | null> {
  const standardBase64 = fromUrlSafeBase64(urlSafeToken);
  const cipherBytes = base64ToBytes(standardBase64);
  if (cipherBytes.length <= 16) return null;

  const iv = cipherBytes.slice(0, 16);
  const ciphertext = cipherBytes.slice(16);

  const keyBytes = deriveKeyBytes(keyString);
  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    keyBytes,
    { name: "AES-CBC" },
    false,
    ["decrypt"]
  );

  const plainBuffer = await crypto.subtle.decrypt(
    { name: "AES-CBC", iv },
    cryptoKey,
    ciphertext
  );

  const json = new TextDecoder("utf-8").decode(plainBuffer);
  return JSON.parse(json) as ProofPdfTokenPayload;
}

function fromUrlSafeBase64(urlSafe: string): string {
  const decoded = decodeURIComponent(urlSafe);
  const swapped = decoded.replace(/-/g, "+").replace(/_/g, "/");
  const padding = swapped.length % 4;
  return padding === 0 ? swapped : swapped + "=".repeat(4 - padding);
}

function base64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

// Mirrors EncryptionHelper.cs:23 — UTF-8 of the key, padded with spaces to 32 chars,
// then truncated to exactly 32 bytes for AES-256.
function deriveKeyBytes(keyString: string): Uint8Array {
  const padded = keyString.padEnd(32, " ").slice(0, 32);
  return new TextEncoder().encode(padded);
}
